/** Rupee display. Whole rupees show no decimals; paisa show two. */
export function formatMoney(amount: number): string {
  const abs = Math.abs(amount)
  const paisa = Math.round(abs * 100) % 100
  const body = abs.toLocaleString('en-PK', {
    minimumFractionDigits: paisa === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })
  return `${amount < 0 ? '-' : ''}Rs ${body}`
}

/** A signed rupee figure, for a variance: an explicit `+` on a surplus and
 * `-` on a shortfall, because "which way is it out?" is the only question a
 * variance is ever asked. Zero carries no sign. A null or non-finite figure
 * is "nobody counted", not zero, and renders the em dash — the Z-report's
 * rule for every nullable figure on the day-close screen.
 *
 * One export rather than a copy per screen: the day-close screen and the
 * Day-closes report tab now show the same row side by side, so the sign
 * convention has to come from one place. */
export function signedMoney(n: number | null | undefined): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—'
  if (n === 0) return formatMoney(0)
  return (n > 0 ? '+' : '-') + formatMoney(Math.abs(n))
}

/** Weight for a screen: always 3 decimals, thousands grouped, kg unit
 * appended ("1,250.000 kg"). Named apart from lib/weight.ts's `formatKg`,
 * which is the ungrouped scale format the pad and the print builders use —
 * the two produce different strings for the same load, so the name has to
 * say which one a call site is asking for. */
export function formatKgGrouped(kg: number): string {
  return `${kg.toLocaleString('en-PK', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg`
}

/** Weight for a chart axis tick: whole kilos, no unit — `formatKgGrouped`'s
 * 3 decimals make a Y-axis (`TrendChart`) wider than the plot it labels.
 * Kept separate rather than a formatting option on it: the tooltip next to
 * the same axis still wants the exact 3-decimal figure. */
export function formatKgTick(kg: number): string {
  return Math.round(kg).toLocaleString('en-PK')
}

/** Auto-compact rupee figure for tight spaces (heatmap cells, stat deltas):
 * 1,284 stays whole; 12,900 becomes 12.9K; 4,200,000 becomes 4.2M. Always
 * whole rupees, no paisa — a compact label is not where paisa is checked. */
export function compactMoney(amount: number): string {
  const abs = Math.abs(amount)
  const sign = amount < 0 ? '-' : ''
  if (abs < 1000) return `${sign}${Math.round(abs).toLocaleString('en-PK')}`
  const divisor = abs >= 1_000_000 ? 1_000_000 : 1000
  const suffix = abs >= 1_000_000 ? 'M' : 'K'
  return `${sign}${(abs / divisor).toFixed(1)}${suffix}`
}
